import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faAnchor, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { globeService } from '../services/globeService';
import './PortSearchAutocomplete.css';

const DEBOUNCE_MS = 280;
const MIN_CHARS = 3;

/**
 * Live port picker backed by /api/globe/port-search/ (NavAPI SeaPortSearch, which
 * bills 0.01 routing tokens per search — 100 searches = 1 token; interactive use only).
 *
 * Debounces keystrokes (~280ms), queries once the input reaches 3 chars, and
 * shows a dropdown of alias-split suggestions. On selection it calls
 * onSelect(port) with the canonical NavAPI hit:
 *   { port_code, port_name, latitude, longitude, country, label }
 * so the caller gets correct coords + code and the port lands exactly right on
 * the map. Loading / empty / no-results / unconfigured states each render a calm
 * inline message rather than a red error. Keyboard nav (up/down/enter/esc) and
 * outside-click close are supported.
 *
 * Free-text is preserved: onChange(value) fires on every keystroke so a caller
 * that also stores raw text keeps working even if the user never picks a
 * suggestion.
 */
const PortSearchAutocomplete = ({
  value = '',
  onSelect,
  onChange,
  placeholder = 'Search port by name or UN/LOCODE…',
  disabled = false,
  className = '',
  inputClassName = '',
  showClearButton = true,
  autoFocus = false,
}) => {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  // 'idle' | 'empty' (valid search, zero matches) | 'error' (request failed)
  // | 'unconfigured' (routing key unset — feature hidden server-side).
  const [searchState, setSearchState] = useState('idle');

  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  // Latest onChange in a ref so the debounced closure never goes stale.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const doSearch = useCallback(async (searchQuery) => {
    const q = (searchQuery || '').trim();
    if (q.length < MIN_CHARS) {
      setResults([]);
      setIsOpen(false);
      setSearchState('idle');
      return;
    }
    setIsLoading(true);
    try {
      const data = await globeService.portSearch(q);
      if (data.configured === false) {
        setResults([]);
        setSearchState('unconfigured');
        setIsOpen(true);
        return;
      }
      const list = data.results || [];
      setResults(list);
      setHighlightIndex(-1);
      setSearchState(list.length > 0 ? 'idle' : 'empty');
      setIsOpen(true);
    } catch (err) {
      console.error('Port search failed:', err);
      setResults([]);
      setSearchState('error');
      setIsOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (onChangeRef.current) onChangeRef.current(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), DEBOUNCE_MS);
  };

  const handleSelect = (port) => {
    setQuery(port.label || port.port_name || '');
    setIsOpen(false);
    setResults([]);
    setSearchState('idle');
    setHighlightIndex(-1);
    if (onSelect) onSelect(port);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSearchState('idle');
    setHighlightIndex(-1);
    if (onChangeRef.current) onChangeRef.current('');
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (!isOpen || results.length === 0) return;
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(results[highlightIndex]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Keep the input in sync when the parent supplies/changes the value after
  // mount (e.g. a recap auto-populates the field). ``value`` only changes on
  // those parent-driven events, never on each keystroke, so this never clobbers
  // what the user is actively typing.
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const showSpinner = isLoading;

  return (
    <div className={`port-search-container ${className}`} ref={containerRef}>
      <div className="port-search-input-wrapper">
        <FontAwesomeIcon icon={faSearch} className="port-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className={`port-search-input ${inputClassName}`}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0 || searchState !== 'idle') setIsOpen(true); }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          autoFocus={autoFocus}
        />
        {showSpinner && <FontAwesomeIcon icon={faSpinner} spin className="port-search-spinner" />}
        {!showSpinner && showClearButton && query && (
          <button
            type="button"
            className="port-search-clear"
            aria-label="Clear port"
            onClick={handleClear}
            tabIndex={-1}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>

      {isOpen && results.length === 0 && searchState === 'empty' && !isLoading && (
        <div className="port-search-dropdown port-search-message">
          <FontAwesomeIcon icon={faAnchor} className="port-search-message-icon" />
          <span className="port-search-message-text">
            No ports found &mdash; try a different name or UN/LOCODE
          </span>
        </div>
      )}

      {isOpen && results.length === 0 && searchState === 'unconfigured' && !isLoading && (
        <div className="port-search-dropdown port-search-message">
          <FontAwesomeIcon icon={faAnchor} className="port-search-message-icon" />
          <span className="port-search-message-text">
            Live port search is not enabled on this environment.
          </span>
        </div>
      )}

      {isOpen && results.length === 0 && searchState === 'error' && !isLoading && (
        <div className="port-search-dropdown port-search-message port-search-message-error">
          <FontAwesomeIcon icon={faSearch} className="port-search-message-icon" />
          <span className="port-search-message-text">
            Port search is temporarily unavailable. Please try again.
          </span>
        </div>
      )}

      {isOpen && results.length > 0 && (
        <ul className="port-search-dropdown">
          {results.map((port, index) => (
            <li
              key={`${port.port_code}-${port.port_name}-${index}`}
              className={`port-search-item ${index === highlightIndex ? 'port-search-item-highlighted' : ''}`}
              onClick={() => handleSelect(port)}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              <div className="port-search-item-left">
                <FontAwesomeIcon icon={faAnchor} className="port-search-anchor-icon" />
                <div className="port-search-item-info">
                  <span className="port-search-item-name">{port.label || port.port_name}</span>
                  {port.country && (
                    <span className="port-search-item-country">{port.country}</span>
                  )}
                </div>
              </div>
              {port.port_code && (
                <span className="port-search-item-code">{port.port_code}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PortSearchAutocomplete;
